import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import TermsModal from './TermsModal';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const isHomePage = location.pathname === '/';

  const handlePrivacyClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isHomePage) {
      e.preventDefault();
      setIsPrivacyModalOpen(true);
    }
  };

  const handleTermsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isHomePage) {
      e.preventDefault();
      setIsTermsModalOpen(true);
    }
  };

  return (
    <>
      <PrivacyPolicyModal
        isOpen={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
      />
      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />
      <footer className="bg-gray-900 text-gray-300">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            {/* Top Section: Logo and Info */}
            <div className="flex flex-wrap items-start justify-between gap-8 mb-6">
              {/* Left: Contact Info */}
              <div className="flex flex-col gap-3">
                {/* Contact Numbers */}
                <div className="flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className="text-sm text-gray-400">{t('footer.rentCar')}</span>
                    <a href="tel:1600-4412" className="text-lg font-bold text-blue-400 hover:text-blue-300">
                      1600-4412
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className="text-sm text-gray-400">{t('footer.vipBus')}</span>
                    <a href="tel:1566-4412" className="text-lg font-bold text-blue-400 hover:text-blue-300">
                      1566-4412
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-gray-400">{t('footer.businessHours')}</span>
                    <span className="text-base font-semibold text-white">{t('footer.businessHoursValue')}</span>
                  </div>
                </div>

                {/* Station Info */}
                <div className="flex flex-col gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-white font-semibold">{t('footer.seomyeon')}</span>
                    <span className="text-gray-400">{t('footer.seomyeonAddress')}</span>
                    <a href="tel:051-804-4412" className="text-blue-400 hover:text-blue-300">
                      051-804-4412
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-white font-semibold">{t('footer.gaya')}</span>
                    <span className="text-gray-400">{t('footer.gayaAddress')}</span>
                    <a href="tel:051-802-4412" className="text-blue-400 hover:text-blue-300">
                      051-802-4412
                    </a>
                  </div>
                </div>
              </div>

              {/* Right: Logo */}
              <img src="/logo_footer.png" alt="굿타임 렌터카" className="h-8" />
            </div>

            {/* Company Info - Compact */}
            <div className="border-t border-gray-700 py-4 text-xs text-gray-400">
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                <span>{t('footer.companyNewGoodtime')}</span>
                <span>{t('footer.representative')}: 강두연</span>
                <span>{t('footer.businessNumber')}: 605-86-04902</span>
                <span className="text-gray-600">|</span>
                <span>{t('footer.companyGoodtimeTour')}</span>
                <span>{t('footer.representative')}: 김민재</span>
                <span>{t('footer.businessNumber')}: 621-81-62554</span>
              </div>
              <div className="mt-2">
                <span>{t('footer.mailOrderNumber')}</span>
              </div>
            </div>

            {/* Bottom: Policy Links & Copyright */}
            <div className="border-t border-gray-700 pt-4">
              <div className="flex flex-wrap items-center justify-between gap-4 text-xs">
                <div className="flex gap-4">
                  <Link
                    to="/terms"
                    onClick={handleTermsClick}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {t('footer.terms')}
                  </Link>
                  <Link
                    to="/privacy"
                    onClick={handlePrivacyClick}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {t('footer.privacy')}
                  </Link>
                </div>
                <p className="text-gray-500">&copy; {new Date().getFullYear()} {t('footer.copyright')}</p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
